import { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import {
  ShieldCheck,
  History,
  Search,
  Loader2,
  AlertCircle,
  ChevronRight,
  Package,
  Apple,
  Sparkles,
  Plus,
  X,
  ScanBarcode,
  Lock,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { isNative } from '@/lib/capacitor';
import { NativeBarcodeScannerModal } from '../components/NativeBarcodeScannerModal';
import { RiskBadge } from '../components/RiskBadge';
import { ProductAddModal } from '../components/ProductAddModal';
import { useProductScanner, type ProductSearchResult, type SearchErrorType } from '../hooks/useProductScanner';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import { useLimits } from '@/hooks/useLimits';
import type { ProductScan, ProductType } from '../types';

export function ScannerPage() {
  const { t } = useTranslation('scanner');
  const navigate = useNavigate();
  const { lookupProduct, searchProducts, getScanHistory, isLoading, isSearching, error } = useProductScanner();
  const isProductCheckEnabled = useFeatureEnabled('feature_product_check');
  const { isLimitEnabled } = useLimits();
  const isBarcodeScannerAllowed = isLimitEnabled('barcode_scanner');

  // All hooks must be called before any conditional returns
  const [productType, setProductType] = useState<ProductType>('food');
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [searchError, setSearchError] = useState<SearchErrorType | null>(null);
  const [visibleCount, setVisibleCount] = useState(6);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentScans, setRecentScans] = useState<ProductScan[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [showScannerPremiumModal, setShowScannerPremiumModal] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [searchPosition, setSearchPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Es gibt etwas zum Anzeigen, wenn entweder Treffer vorhanden sind ODER ein
  // searchError vorliegt - eine Fehlermeldung soll auch dann erscheinen, wenn
  // OFF fehlschlaegt und es zusaetzlich keine lokalen Treffer gibt. Ersetzt an
  // allen bisherigen Stellen die reine Pruefung auf searchResults.length > 0.
  const hasSuggestionContent = searchResults.length > 0 || !!searchError;

  // Load recent scans on mount and when product type changes
  useEffect(() => {
    const loadRecent = async () => {
      setLoadingRecent(true);
      const scans = await getScanHistory(5, productType);
      setRecentScans(scans);
      setLoadingRecent(false);
    };
    loadRecent();
  }, [getScanHistory, productType]);

  // Clear search when product type changes
  useEffect(() => {
    setSearchInput('');
    setSearchResults([]);
    setSearchError(null);
    setShowSuggestions(false);
  }, [productType]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const trimmedInput = searchInput.trim();

    if (/^\d+$/.test(trimmedInput)) {
      setSearchResults([]);
      setSearchError(null);
      setShowSuggestions(false);
      return;
    }

    // Mindestlaenge 3 (vorher 2) und Debounce 500ms (vorher 300ms) - beides
    // reduziert die Anzahl der Anfragen gegen das von OFF geteilte
    // IP-Rate-Limit (10/Minute fuer die gesamte App), ohne die Suche fuer den
    // Nutzer spuerbar traege zu machen.
    if (trimmedInput.length < 3) {
      setSearchResults([]);
      setSearchError(null);
      setShowSuggestions(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setVisibleCount(6);
      const { products, searchError: outcomeError } = await searchProducts(trimmedInput, 12, productType);
      setSearchResults(products);
      setSearchError(outcomeError ?? null);
      // Auch bei reinem Fehler (keine lokalen Treffer) soll der Dropdown-
      // Bereich erscheinen, damit die Fehlermeldung sichtbar wird - siehe
      // hasSuggestionContent weiter unten.
      setShowSuggestions(products.length > 0 || !!outcomeError);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput, searchProducts, productType]);

  // Track search container position for portal rendering
  useLayoutEffect(() => {
    if (placeholderRef.current && showSuggestions && hasSuggestionContent) {
      const rect = placeholderRef.current.getBoundingClientRect();
      setSearchPosition({ top: rect.top, left: rect.left, width: rect.width });
    }
  }, [showSuggestions, hasSuggestionContent]);

  // Refocus input after portal renders
  const isSearchActiveForFocus = showSuggestions && hasSuggestionContent && !showAddModal;
  useEffect(() => {
    if (isSearchActiveForFocus && inputRef.current) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isSearchActiveForFocus]);

  // Update position on scroll/resize when search is active
  useEffect(() => {
    if (!showSuggestions || !hasSuggestionContent) return;

    const updatePosition = () => {
      if (placeholderRef.current) {
        const rect = placeholderRef.current.getBoundingClientRect();
        setSearchPosition({ top: rect.top, left: rect.left, width: rect.width });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSuggestions, hasSuggestionContent]);

  const handleScan = useCallback(async (barcode: string) => {
    const result = await lookupProduct(barcode, productType);

    if (result) {
      navigate(`/tools/scanner/product/${barcode}`, {
        state: { productData: result, productType },
      });
    }
  }, [lookupProduct, navigate, productType]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (trimmed && /^\d{8,14}$/.test(trimmed)) {
      setShowSuggestions(false);
      handleScan(trimmed);
    }
  };

  const handleProductSelect = (product: ProductSearchResult) => {
    setShowSuggestions(false);
    setSearchInput('');
    handleScan(product.barcode);
  };

  const handleInputChange = (value: string) => {
    setSearchInput(value);
  };

  // Show disabled message if feature is off (after all hooks)
  if (!isProductCheckEnabled) {
    return (
      <FeatureDisabled
        featureName={t('featureDisabled.name')}
        description={t('featureDisabled.description')}
      />
    );
  }

  const isSearchActive = showSuggestions && hasSuggestionContent && !showAddModal;
  const canUseScanner = isNative && isBarcodeScannerAllowed;

  // Search container content - reused in normal and portal rendering
  const searchContainerContent = (
    <div className="flex items-center gap-3" ref={searchContainerRef}>
      <form onSubmit={handleManualSubmit} className="flex-1 relative">
        <div className="bg-white rounded-[15px] flex items-center overflow-hidden">
          <input
            ref={inputRef}
            type="text"
            placeholder={productType === 'food' ? t('searchPlaceholder.food') : t('searchPlaceholder.cosmetic')}
            value={searchInput}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => hasSuggestionContent && setShowSuggestions(true)}
            className="flex-1 px-4 py-3 text-pp-dark placeholder-pp-dark/50 text-sm focus:outline-none"
            autoComplete="off"
          />
          {searchInput ? (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setShowSuggestions(false);
              }}
              className="px-3 text-pp-dark/50 hover:text-pp-dark"
            >
              <X className="w-5 h-5" />
            </button>
          ) : canUseScanner ? (
            <button
              type="button"
              onClick={() => setShowScannerModal(true)}
              className="px-3 text-pp-orange hover:text-pp-orange/80 transition-colors"
            >
              {isSearching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <ScanBarcode className="w-5 h-5" />
              )}
            </button>
          ) : isNative && !isBarcodeScannerAllowed ? (
            <button
              type="button"
              onClick={() => setShowScannerPremiumModal(true)}
              className="px-3 text-pp-dark/50 hover:text-pp-orange transition-colors"
              title={t('premiumScanner')}
            >
              <ScanBarcode className="w-5 h-5" />
            </button>
          ) : (
            <div className="px-3 text-pp-dark/50">
              {isSearching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </div>
          )}
        </div>

        {/* Search Suggestions Dropdown */}
        {isSearchActive && (
          <div className="absolute top-full left-0 right-0 mt-2 glass-effect rounded-[15px] overflow-hidden">
            <div className="max-h-[360px] overflow-y-auto overscroll-contain">
              {/* Fehlermeldung fuer die externe Suche (OFF/OBF) - erscheint
                  zusaetzlich zu, nicht statt vorhandener lokaler Treffer.
                  "keine Treffer" und "Suche gerade nicht erreichbar" waren
                  vorher fuer den Nutzer identisch (beides eine leere Liste). */}
              {searchError && (
                <div className="flex items-start gap-2.5 p-3 border-b border-white/10 bg-amber-500/10">
                  <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200/90">
                    {searchError === 'rate_limited' ? t('search.rateLimited') : t('search.unavailable')}
                  </p>
                </div>
              )}
              {searchResults.slice(0, visibleCount).map((product) => (
                <button
                  key={product.barcode}
                  type="button"
                  onClick={() => handleProductSelect(product)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors border-b border-white/10 last:border-0"
                >
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {product.image_small_url ? (
                      <img
                        src={product.image_small_url}
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Package className="w-5 h-5 text-white/40" />
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-medium text-white truncate text-sm">
                      {product.name}
                    </p>
                    <p className="text-xs text-white/50 truncate">
                      {product.brand || product.barcode}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-pp-orange flex-shrink-0" />
                </button>
              ))}
              {searchResults.length > visibleCount && (
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + 6)}
                  className="w-full flex items-center justify-center gap-2 p-3 text-pp-orange hover:bg-white/5 transition-colors font-medium text-sm"
                >
                  <Search className="w-4 h-4" />
                  {t('search.loadMore')}
                </button>
              )}
            </div>
          </div>
        )}
      </form>

      {/* Add Button */}
      <button
        type="button"
        onClick={() => {
          setShowSuggestions(false);
          setShowAddModal(true);
        }}
        className="flex items-center justify-center w-12 h-12 bg-pp-orange hover:bg-pp-orange/90 text-white rounded-[15px] transition-colors flex-shrink-0"
        title={t('reportProduct')}
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );

  return (
    <>
      {/* Search Backdrop + Container - rendered via portal for iOS z-index fix */}
      {isSearchActive && searchPosition && createPortal(
        <>
          <div
            className="fixed inset-0 bg-pp-dark/60 backdrop-blur-sm z-[9998]"
            onClick={() => setShowSuggestions(false)}
          />
          <div
            className="fixed z-[9999]"
            style={{
              top: searchPosition.top,
              left: searchPosition.left,
              width: searchPosition.width,
            }}
          >
            {searchContainerContent}
          </div>
        </>,
        document.body
      )}

      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-6">{t('pageTitle')}</h1>

      {/* Product Type Slider */}
      <div className="glass-effect rounded-[15px] p-1 mb-6 relative">
        {/* Sliding background */}
        <div
          className={`absolute top-1 bottom-1 w-[calc(50%-0.25rem)] bg-pp-orange rounded-[15px] transition-transform duration-300 ease-out ${
            productType === 'cosmetic' ? 'translate-x-[calc(100%+0.25rem)]' : 'translate-x-0'
          }`}
        />
        <div className="relative flex">
          <button
            onClick={() => setProductType('food')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-[15px] transition-colors z-10"
          >
            <Apple className="w-5 h-5 text-white" />
            <span className="font-medium text-white text-sm">{t('productTypes.food')}</span>
          </button>
          <button
            onClick={() => setProductType('cosmetic')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-[15px] transition-colors z-10"
          >
            <Sparkles className="w-5 h-5 text-white" />
            <span className="font-medium text-white text-sm">{t('productTypes.cosmetic')}</span>
          </button>
        </div>
      </div>

      {/* Search Input - Placeholder when portal is active, normal when not */}
      <div
        ref={placeholderRef}
        className={`mb-6 ${isSearchActive ? 'invisible' : ''}`}
      >
        {searchContainerContent}
      </div>

      {/* Barcode submit button */}
      {/^\d{8,14}$/.test(searchInput.trim()) && (
        <button
          onClick={handleManualSubmit}
          className="w-full py-3 mb-6 bg-pp-orange hover:bg-pp-orange/80 text-white font-medium rounded-[15px] flex items-center justify-center gap-2 transition-colors"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('search.searching')}
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" />
              {t('search.checkBarcode')}
            </>
          )}
        </button>
      )}

      {/* Error Message */}
      {error && (
        <div className="glass-effect rounded-[15px] p-4 mb-6 border border-red-500/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-300">{t('search.error')}</p>
              <p className="text-sm text-red-300/70">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Scans */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-white/80">{t('recent.title')}</h2>
          {recentScans.length > 0 && (
            <button
              onClick={() => navigate('/tools/scanner/history')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-pp-orange rounded-[5px] text-white text-xs font-medium"
            >
              <History className="w-3.5 h-3.5" />
              {t('recent.showAll')}
            </button>
          )}
        </div>

        {loadingRecent ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
          </div>
        ) : recentScans.length > 0 ? (
          <div className="divide-y divide-white/10">
            {recentScans.map((scan) => (
              <button
                key={scan.id}
                className="w-full py-3 hover:bg-white/5 transition-all flex items-center gap-3"
                onClick={() => navigate(`/tools/scanner/product/${scan.barcode}`)}
              >
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {scan.product?.image_small_url ? (
                    <img
                      src={scan.product.image_small_url}
                      alt={scan.product.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Package className="w-6 h-6 text-white/40" />
                  )}
                </div>

                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-white truncate">
                    {scan.product?.name || t('recent.unknownProduct')}
                  </p>
                  <p className="text-xs text-white/50 truncate">
                    {scan.product?.brand || scan.barcode}
                  </p>
                </div>

                <RiskBadge risk={scan.scan_result} size="sm" showLabel={false} className="!bg-transparent" />
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-[15px] p-8 text-center">
            <p className="text-white/50 text-sm">
              {t('recent.empty')}
            </p>
            <p className="text-white/30 text-xs mt-1">
              {t('recent.emptyHint')}
            </p>
          </div>
        )}
      </section>

      {/* Add Modal */}
      <ProductAddModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        productType={productType}
      />

      {/* Native Barcode Scanner Modal */}
      {canUseScanner && (
        <NativeBarcodeScannerModal
          isOpen={showScannerModal}
          onClose={() => setShowScannerModal(false)}
          onScan={handleScan}
        />
      )}

      {/* Scanner Premium Modal */}
      {showScannerPremiumModal && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={() => setShowScannerPremiumModal(false)} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              <div className="bottom-sheet-header">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-pp-orange/20 rounded-full flex items-center justify-center">
                    <ScanBarcode className="w-5 h-5 text-pp-orange" />
                  </div>
                  <h2>{t('premiumScannerTitle')}</h2>
                </div>
                <button
                  onClick={() => setShowScannerPremiumModal(false)}
                  className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="bottom-sheet-content text-center py-6">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-pp-orange" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('premiumScannerTitle')}</h3>
                <p className="text-white/60">
                  {t('premiumScannerDescription')}
                </p>
              </div>
              <div className="bottom-sheet-footer">
                <Link
                  to="/subscription"
                  onClick={() => setShowScannerPremiumModal(false)}
                  className="w-full py-3 bg-pp-orange text-white font-medium rounded-xl text-center hover:bg-pp-orange/90 transition-colors"
                >
                  {t('premiumScannerUpgrade')}
                </Link>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
